<script>
// Forward attrs (aria-*, etc.) to the inner <input>, not the wrapper <div>.
export default { inheritAttrs: false };
</script>

<script setup>
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
const { t } = useI18n();

const props = defineProps({
  modelValue: {
    required: true,
  },
  modelLabel: {
    type: String,
    default: "",
  },
  modelName: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    required: false,
  },
  type: {
    type: String,
    required: true,
  },
  color: {
    type: String,
    default: "grape",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  validationError: {
    type: String,
  },
  autocomplete: {
    type: Boolean,
    default: (import.meta.env?.VITE_AUTOCOMPLETE || "off") === "on",
    required: false,
  },
  // Native autocomplete token (e.g. "one-time-code", "username"). Overrides
  // the boolean `autocomplete` when set. Required for iOS to show the SMS /
  // Authenticator autofill chip on TOTP inputs.
  autocompleteHint: {
    type: String,
    default: "",
    required: false,
  },
  inputmode: {
    type: String,
    default: "",
    required: false,
  },
  pattern: {
    type: String,
    default: "",
    required: false,
  },
  maxlength: {
    type: [Number, String],
    default: null,
    required: false,
  },
});

defineEmits(["update:modelValue"]);

const input = ref(null);

onMounted(() => {
  if (input.value.hasAttribute("autofocus")) {
    input.value.focus();
  }
});

defineExpose({ focus: () => input.value.focus() });
</script>

<template>
  <div>
    <label :for="modelName" class="block text-sm font-medium leading-6">{{
      t(modelLabel == "" ? modelName : modelLabel)
    }}</label>
    <div class="relative mt-2">
      <input
        v-bind="$attrs"
        :name="modelName"
        :id="modelName"
        :value="modelValue"
        :disabled="disabled"
        @input="$emit('update:modelValue', $event.target.value)"
        ref="input"
        :type="type"
        class="peer block w-full border-0 bg-background py-1.5 focus:outline-none focus:ring-0 sm:text-sm sm:leading-6"
        :placeholder="placeholder"
        :autocomplete="autocompleteHint || (autocomplete ? 'on' : 'off')"
        :inputmode="inputmode || undefined"
        :pattern="pattern || undefined"
        :maxlength="maxlength != null ? maxlength : undefined"
      />
      <div
        class="absolute inset-x-0 bottom-0 border-t border-[#ccd1c4] peer-focus:border-t-2"
        :class="{
          'peer-focus:border-theme-teal': color == 'teal',
          'peer-focus:border-theme-teal-hc': color == 'teal-hc',
          'peer-focus:border-theme-grape': color == 'grape',
          'peer-focus:border-theme-grape-hc': color == 'grape-hc',
          'peer-focus:border-theme-iceberg': color == 'iceberg',
          'peer-focus:border-theme-iceberg-hc': color == 'iceberg-hc',
          'peer-focus:border-theme-rose': color == 'rose',
          'peer-focus:border-theme-rose-hc': color == 'rose-hc',
          'peer-focus:border-theme-offsite': color == 'offsite',
          'peer-focus:border-theme-offsite-hc': color == 'offsite-hc',
        }"
        aria-hidden="true"
      />
    </div>
    <div v-if="validationError" class="text-xs text-danger">
      {{ validationError }}
    </div>
  </div>
</template>
